import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

// Cumulative P/L by trade index (62 trades, ending ~+7700)
const cumulativePL = [
  { i: 1, v: -120 }, { i: 2, v: -85 }, { i: 3, v: 210 }, { i: 4, v: 180 },
  { i: 5, v: 140 }, { i: 6, v: 520 }, { i: 7, v: 490 }, { i: 8, v: 450 },
  { i: 9, v: 410 }, { i: 10, v: 750 }, { i: 11, v: 720 }, { i: 12, v: 680 },
  { i: 13, v: 1100 }, { i: 14, v: 1050 }, { i: 15, v: 1000 }, { i: 16, v: 960 },
  { i: 17, v: 1350 }, { i: 18, v: 1300 }, { i: 19, v: 1680 }, { i: 20, v: 1650 },
  { i: 21, v: 1620 }, { i: 22, v: 2050 }, { i: 23, v: 2000 }, { i: 24, v: 1950 },
  { i: 25, v: 2400 }, { i: 26, v: 2350 }, { i: 27, v: 2300 }, { i: 28, v: 2700 },
  { i: 29, v: 2650 }, { i: 30, v: 2600 }, { i: 31, v: 3050 }, { i: 32, v: 3000 },
  { i: 33, v: 2950 }, { i: 34, v: 2900 }, { i: 35, v: 2850 }, { i: 36, v: 3300 },
  { i: 37, v: 3600 }, { i: 38, v: 3550 }, { i: 39, v: 3500 }, { i: 40, v: 3900 },
  { i: 41, v: 3850 }, { i: 42, v: 4250 }, { i: 43, v: 4600 }, { i: 44, v: 4550 },
  { i: 45, v: 4500 }, { i: 46, v: 4900 }, { i: 47, v: 4850 }, { i: 48, v: 5250 },
  { i: 49, v: 5600 }, { i: 50, v: 5550 }, { i: 51, v: 5500 }, { i: 52, v: 5900 },
  { i: 53, v: 6300 }, { i: 54, v: 6250 }, { i: 55, v: 6600 }, { i: 56, v: 6550 },
  { i: 57, v: 6900 }, { i: 58, v: 7250 }, { i: 59, v: 7200 }, { i: 60, v: 7150 },
  { i: 61, v: 7500 }, { i: 62, v: 7700 },
];

// Loss streak distribution histogram
const lossStreakDist = [
  { length: 1, count: 8 },
  { length: 2, count: 5 },
  { length: 3, count: 3 },
  { length: 4, count: 1 },
  { length: 5, count: 1 },
  { length: 9, count: 1 },
];

export default function BehaviourAnalysisMock() {
  const sectionHeader: React.CSSProperties = {
    fontSize: '0.5rem',
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.375rem',
  };

  const metricRow: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '0.125rem 0',
    fontSize: '0.6875rem',
  };

  const barMaxWidth = 260;
  const avgWin = 836;
  const avgLoss = 425;
  const bestWin = 2418;
  const worstLoss = 1305;
  const maxVal = bestWin;

  const barRow = (
    label: string,
    value: number,
    color: string,
    prefix: string,
  ) => (
    <div style={{ marginBottom: '0.375rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.625rem', color: '#9ca3af', marginBottom: 2 }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: prefix === '+' ? '#34d399' : '#f87171' }}>
          {prefix}{value.toLocaleString()} USD
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#1f2937', width: barMaxWidth }}>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: color,
            width: `${(value / maxVal) * 100}%`,
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="platform-mock" style={{ maxWidth: '100%' }}>
      <div className="platform-mock-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span><span className="dot" style={{ background: '#f59e0b' }} /> Trading Behaviour Analysis</span>
        <span style={{ fontSize: '0.75rem', color: '#4b5563', cursor: 'default' }}>x</span>
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem 1rem' }}>
        {/* Subtitle */}
        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
          Psychological and structural patterns derived from 62 trades
        </div>

        {/* 2x2 grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '0.5rem' }}>
          {/* Panel 1: Win/Loss Asymmetry */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Win/Loss Asymmetry</div>
            {barRow('Avg Win', avgWin, '#34d399', '+')}
            {barRow('Avg Loss', avgLoss, '#f87171', '-')}
            {barRow('Best Win', bestWin, '#059669', '+')}
            {barRow('Worst Loss', worstLoss, '#e11d48', '-')}
            <div style={{ fontSize: '0.5rem', color: '#4b5563', fontStyle: 'italic', marginTop: 4 }}>
              Wins are materially larger than losses — payoff-driven behaviour
            </div>
          </div>

          {/* Panel 2: Streak Behaviour */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Streak Behaviour</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div>
                <div style={{ fontSize: '0.5rem', color: '#9ca3af' }}>Max Loss Streak</div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f87171', fontFamily: 'monospace' }}>9</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.5rem', color: '#9ca3af' }}>Max Win Streak</div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#34d399', fontFamily: 'monospace' }}>3</div>
              </div>
            </div>
            <div style={{ ...sectionHeader, marginTop: '0.25rem' }}>Loss Streak Distribution</div>
            <ResponsiveContainer width="100%" height={50}>
              <BarChart data={lossStreakDist} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <XAxis dataKey="length" tick={{ fill: '#4b5563', fontSize: 8 }} axisLine={false} tickLine={false} />
                <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                  {lossStreakDist.map((entry, index) => (
                    <Cell key={index} fill={entry.length >= 5 ? '#f87171' : '#f8717180'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: '0.5rem', color: '#4b5563', fontStyle: 'italic', marginTop: 2 }}>
              Extended losing streaks require psychological preparation
            </div>
          </div>

          {/* Panel 3: Trade Outcome Sequencing */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Trade Outcome Sequencing</div>
            <ResponsiveContainer width="100%" height={85}>
              <AreaChart data={cumulativePL} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="cum-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="i"
                  tick={{ fill: '#4b5563', fontSize: 7 }}
                  axisLine={false}
                  tickLine={false}
                  interval={3}
                />
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke="#818cf8"
                  strokeWidth={1.5}
                  fill="url(#cum-grad)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ fontSize: '0.5rem', color: '#4b5563', fontStyle: 'italic', marginTop: 2 }}>
              Cumulative P/L by trade index — reveals flat periods and equity jumps
            </div>
          </div>

          {/* Panel 4: Behaviour Profile */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Behaviour Profile</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <div style={metricRow}>
                <span style={{ color: '#9ca3af' }}>Edge Type</span>
                <span style={{ fontWeight: 700, color: '#e5e7eb' }}>Balanced</span>
              </div>
              <div style={metricRow}>
                <span style={{ color: '#9ca3af' }}>Win Dependence</span>
                <span style={{ fontWeight: 700, color: '#e5e7eb' }}>Low frequency</span>
              </div>
              <div style={metricRow}>
                <span style={{ color: '#9ca3af' }}>Streak Sensitivity</span>
                <span style={{ fontWeight: 700, color: '#f87171' }}>High</span>
              </div>
              <div style={metricRow}>
                <span style={{ color: '#9ca3af' }}>Holding Tolerance</span>
                <span style={{ fontWeight: 700, color: '#e5e7eb' }}>High patience required</span>
              </div>
              <div style={metricRow}>
                <span style={{ color: '#9ca3af' }}>Recovery Style</span>
                <span style={{ fontWeight: 700, color: '#e5e7eb' }}>Gradual</span>
              </div>
            </div>
          </div>
        </div>

        {/* Interpretation block */}
        <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.75rem', border: '1px solid #1f2937', marginTop: '0.5rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#e5e7eb', marginBottom: '0.5rem' }}>
            Observed Behaviour Interpretation
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <div style={{ ...sectionHeader, color: '#9ca3af' }}>Characteristics</div>
              <div style={{ fontSize: '0.6875rem', color: '#d1d5db', lineHeight: 1.7 }}>
                Balanced win/loss magnitude relationship<br />
                Prolonged losing streaks are part of normal operation
              </div>
            </div>
            <div>
              <div style={{ ...sectionHeader, color: '#9ca3af' }}>Implications</div>
              <div style={{ fontSize: '0.6875rem', color: '#d1d5db', lineHeight: 1.7 }}>
                Moderate tolerance for win rate variation<br />
                Best suited to patient capital
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Additional Statistics */}
        <div style={{
          background: '#0f1a28',
          borderRadius: 6,
          padding: '0.5rem 0.75rem',
          border: '1px solid #1f2937',
          marginTop: '0.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.6875rem', fontWeight: 700, color: '#e5e7eb' }}>
            <span>Additional Statistics</span>
            <span style={{ fontSize: '0.5rem', color: '#4b5563' }}>^</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginTop: '0.375rem' }}>
            {[
              { label: 'Win Count', value: '27' },
              { label: 'Loss Count', value: '35' },
              { label: 'Median Loss Streak', value: '1' },
              { label: 'Trades/Month', value: '0.6' },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontSize: '0.5rem', color: '#6b7280' }}>{s.label}</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#e5e7eb', fontFamily: 'monospace' }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        The Trading Behaviour Analysis modal — psychological and structural patterns including win/loss asymmetry, streak behaviour, trade sequencing, and automated behavioural profiling.
      </div>
    </div>
  );
}
